import { Controller } from "@hotwired/stimulus"

// StylePresetsController — handles the "quick preset" picker tile click.
// On change, reads the preset's style_hash off the radio's
// data-style-presets-style-json attribute, writes each value into the
// matching hidden input (data-style-presets-target="hidden<Key>"), and
// dispatches `input` + `change` events so the live preview controllers
// (palette-live-preview + per-template controller) react.
//
// This controller is intentionally narrow — it does NOT touch the
// preview itself. The hidden inputs carry `data-live-preview-field` so
// the existing reactive infrastructure picks the change up automatically.
//
// CONTRACT: each tile radio must carry data-style-presets-style-json
// with a JSON-encoded Hash of the five common style keys + type_pair.
// Each hidden input must carry both data-style-presets-target (so we
// can find it) AND data-live-preview-field (so the listeners react).
export default class StylePresetsController extends Controller {
  static targets = [
    "radio",
    "hiddenTypePair",
    "hiddenFontSizeScale",
    "hiddenTextStrokeWidth",
    "hiddenTextStrokeColor",
    "hiddenTextColor",
    "hiddenTextShadow",
  ]

  connect() {
    this.boundChange = this.handleChange.bind(this)
    this.radioTargets.forEach((radio) =>
      radio.addEventListener("change", this.boundChange),
    )
  }

  disconnect() {
    this.radioTargets.forEach((radio) =>
      radio.removeEventListener("change", this.boundChange),
    )
  }

  handleChange(event) {
    const radio = event.target
    if (!radio.checked) return
    if (!radio.dataset.stylePresetsStyleJson) return

    let styleHash
    try {
      styleHash = JSON.parse(radio.dataset.stylePresetsStyleJson)
    } catch (e) {
      return
    }

    // Map the camelCase target names to the snake_case keys in the
    // preset payload. Each entry: [hidden target name, payload key,
    // serializer]. Serializer normalises the value into a string the
    // hidden input can carry — booleans become "true"/"false" so they
    // round-trip through Rails strong params correctly.
    const fields = [
      ["hiddenTypePair",         "type_pair",          (v) => String(v ?? "")],
      ["hiddenFontSizeScale",    "font_size_scale",    (v) => String(v ?? "")],
      ["hiddenTextStrokeWidth",  "text_stroke_width",  (v) => String(v ?? "")],
      ["hiddenTextStrokeColor",  "text_stroke_color",  (v) => String(v ?? "")],
      ["hiddenTextColor",        "text_color",         (v) => String(v ?? "")],
      ["hiddenTextShadow",       "text_shadow",        (v) => (v ? "true" : "false")],
    ]

    fields.forEach(([targetName, payloadKey, serializer]) => {
      const target = this[`${targetName}Target`] || null
      if (!target) return
      if (!(payloadKey in styleHash)) return
      const next = serializer(styleHash[payloadKey])
      if (target.value === next) return
      target.value = next
      // Find the user-facing input that carries this preview field
      // (`type="range"` slider, `type="color"` picker, `type="radio"`
      // group, or `type="checkbox"`) and propagate the value there too.
      // Without this, the live-preview controllers read from this
      // controller's own (hidden) target instead of the user's actual
      // input — meaning subsequent slider drags appear to do nothing.
      // The hidden input keeps `data-style-preset-key` only; the
      // user-facing input uniquely carries `data-live-preview-field`.
      this.propagateToUserFacing(payloadKey, next)
    })
  }

  // Mirror the preset value onto the user-facing input that carries
  // `data-live-preview-field="<key>"`. For checkboxes we set checked,
  // for radios we set the matching option to checked, for everything
  // else we set value. Then dispatch input + change so live-preview
  // controllers react.
  propagateToUserFacing(key, valueAsString) {
    const candidates = this.element.parentElement
      ? this.element.parentElement.querySelectorAll(
          `[data-live-preview-field="${key}"]`,
        )
      : []
    if (!candidates.length) {
      // Picker may live OUTSIDE this controller's element scope (the
      // preset strip is rendered above the picker section). Fall back
      // to a document-wide query.
      const wide = document.querySelectorAll(
        `[data-live-preview-field="${key}"]`,
      )
      if (!wide.length) return
      this.applyToInputs(Array.from(wide), key, valueAsString)
      return
    }
    this.applyToInputs(Array.from(candidates), key, valueAsString)
  }

  applyToInputs(inputs, key, valueAsString) {
    inputs.forEach((input) => {
      if (input.type === "checkbox") {
        const next = valueAsString === "true" || valueAsString === "1"
        if (input.checked !== next) {
          input.checked = next
          input.dispatchEvent(new Event("change", { bubbles: true }))
        }
        return
      }
      if (input.type === "radio") {
        const shouldCheck = input.value === valueAsString
        if (shouldCheck && !input.checked) {
          input.checked = true
          input.dispatchEvent(new Event("change", { bubbles: true }))
        }
        return
      }
      if (input.value === valueAsString) return
      input.value = valueAsString
      input.dispatchEvent(new Event("input", { bubbles: true }))
      input.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  // Helper so tests can probe state without using `this[`${name}Target`]`
  // syntax — Stimulus auto-generates `has<Name>Target` and `<name>Target`
  // accessors for static targets above. Wrapped here for readability.
  hasTarget(name) {
    return this[`has${name.charAt(0).toUpperCase() + name.slice(1)}Target`]
  }
}
